import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import {
  ArrowRight,
  Target,
  Eye,
  Shield,
  Award,
  BadgeCheck,
  Users,
  HeartHandshake,
  Sparkles,
  Globe2,
  Hammer,
  CheckCircle2,
  Phone
} from 'lucide-react';

const fadeUpVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};

export default function About() {
  const storyRef = useRef(null);
  const missionRef = useRef(null);
  const credentialsRef = useRef(null);
  const teamRef = useRef(null);

  const storyInView = useInView(storyRef, { once: true, margin: '-100px' });
  const missionInView = useInView(missionRef, { once: true, margin: '-100px' });
  const credentialsInView = useInView(credentialsRef, { once: true, margin: '-100px' });
  const teamInView = useInView(teamRef, { once: true, margin: '-100px' });

  const values = [
    {
      icon: Shield,
      title: 'Quality & Excellence',
      description:
        'Uncompromising standards in every repair and build. We never compromise on materials or workmanship.'
    },
    {
      icon: Eye,
      title: 'Transparency',
      description:
        'Clear communication, honest pricing, and full visibility throughout the project lifecycle.'
    },
    {
      icon: HeartHandshake,
      title: 'Customer Satisfaction',
      description:
        'We measure success by policyholder and client satisfaction at every touchpoint.'
    },
    {
      icon: Globe2,
      title: 'Social Responsibility',
      description:
        '100% Black-Owned and committed to empowerment, transformation, and our communities.'
    }
  ];

  const credentials = [
    {
      icon: Award,
      title: 'Level 1 B-BBEE Contributor',
      description: 'Certified empowerment status.'
    },
    {
      icon: BadgeCheck,
      title: '100% Black-Owned',
      description: 'Proudly South African company.'
    },
    {
      icon: Hammer,
      title: 'NHBRC Registered',
      description: 'National Home Builders Registration.'
    },
    {
      icon: CheckCircle2,
      title: 'PIRB Registration #2925/10',
      description: 'Certified plumbing services.'
    },
    {
      icon: Shield,
      title: 'Type 3 Asbestos Certified',
      description: 'High-risk asbestos removal.'
    },
    {
      icon: Sparkles,
      title: 'COIDA Compliant',
      description: 'Comprehensive insurance coverage.'
    }
  ];

  const team = [
    {
      initials: 'MB',
      name: 'Moazzum Begg',
      role: 'Customer Service and Operations',
      phone: '082 569 7495',
      tel: '0825697495'
    },
    {
      initials: 'SS',
      name: 'Sedick Samsodien',
      role: 'Customer Service and Quotes',
      phone: '069 023 6511',
      tel: '0690236511'
    },
    {
      initials: 'MRS',
      name: 'Mogamad Raee Samsodien',
      role: 'Project and Site Co-Ordinator',
      phone: '069 607 9563',
      tel: '0696079563'
    }
  ];

  return (
    <main data-testid="about-page">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_mobile-first-build-48/artifacts/twvbzukk_Work%20Background.jpg"
            alt="MB Construction site"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/80 to-background" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-32">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="overline mb-6 block">About Us</span>
            <h1 className="font-heading font-light text-5xl sm:text-6xl md:text-7xl text-text-main tracking-tight">
              About <span className="text-primary">MB Construction</span>
            </h1>
            <p className="mt-8 max-w-2xl text-text-muted font-body text-base md:text-lg leading-relaxed">
              Building partnerships and delivering quality construction solutions across South
              Africa since our inception.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Who We Are Section */}
      <section
        ref={storyRef}
        data-testid="story-section"
        className="py-24 md:py-32 lg:py-48 bg-background"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              animate={storyInView ? 'visible' : 'hidden'}
            >
              <motion.span variants={fadeUpVariants} className="overline">
                01 // Who We Are
              </motion.span>
              <motion.h2
                variants={fadeUpVariants}
                className="font-heading font-light text-4xl md:text-5xl text-text-main mt-4 tracking-tight"
              >
                A Specialist<br />
                <span className="text-primary">Contractor</span>
              </motion.h2>
              <motion.div
                variants={fadeUpVariants}
                className="mt-8 space-y-6 text-text-muted font-body leading-relaxed"
              >
                <p>
                  MBConstruction is a{' '}
                  <span className="text-text-main">100% Black-Owned, Level 1 B-BBEE</span> specialist
                  contractor serving the South African insurance, intermediary, and construction
                  markets.
                </p>
                <p>
                  We combine over{' '}
                  <span className="text-text-main">30 years of collective experience</span> from our
                  key personnel with modern claim management technology to deliver efficient,
                  transparent, and high-quality repair and construction solutions.
                </p>
                <p>
                  Our turnkey approach reduces claim lifecycle times, enhances policyholder
                  satisfaction, and supports our institutional and commercial clients on major
                  projects across the Western Cape and nationally.
                </p>
              </motion.div>
              <motion.div variants={fadeUpVariants} className="mt-10 flex gap-8">
                <div>
                  <div className="font-heading font-light text-5xl text-primary">100%</div>
                  <div className="font-mono text-xs uppercase tracking-widest text-text-muted mt-2">
                    Black-Owned
                  </div>
                </div>
                <div>
                  <div className="font-heading font-light text-5xl text-primary">Level 1</div>
                  <div className="font-mono text-xs uppercase tracking-widest text-text-muted mt-2">
                    B-BBEE Status
                  </div>
                </div>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={storyInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_mobile-first-build-48/artifacts/g3o47knq_working%20guys%201.jpg"
                  alt="MB Construction team"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-8 -left-8 bg-surface border border-border p-8">
                <Award className="text-primary mb-2" size={32} strokeWidth={1} />
                <div className="font-mono text-xs uppercase tracking-widest text-text-muted">
                  Certified Excellence
                </div>
                <div className="font-heading text-xl text-text-main mt-1">Insurance Repair</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission / Vision / Values */}
      <section
        ref={missionRef}
        data-testid="mission-section"
        className="py-24 md:py-32 bg-surface border-y border-border"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate={missionInView ? 'visible' : 'hidden'}
          >
            <motion.span variants={fadeUpVariants} className="overline">
              02 // Mission & Vision
            </motion.span>
            <motion.h2
              variants={fadeUpVariants}
              className="font-heading font-light text-4xl md:text-5xl text-text-main mt-4 tracking-tight"
            >
              What Drives Us
            </motion.h2>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate={missionInView ? 'visible' : 'hidden'}
            className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12"
          >
            <motion.div
              variants={fadeUpVariants}
              className="bg-background border border-border p-8 md:p-12"
            >
              <Target className="text-primary mb-6" size={36} strokeWidth={1} />
              <h3 className="font-heading text-2xl md:text-3xl text-text-main mb-4 tracking-tight">
                Our Mission
              </h3>
              <p className="text-text-muted font-body leading-relaxed">
                To deliver efficient, transparent, and high-quality repair solutions that enhance
                policyholder satisfaction and reduce claim lifecycle times. To provide clients with
                an effective turn-key construction consulting solution.
              </p>
            </motion.div>

            <motion.div
              variants={fadeUpVariants}
              className="bg-background border border-border p-8 md:p-12"
            >
              <Eye className="text-primary mb-6" size={36} strokeWidth={1} />
              <h3 className="font-heading text-2xl md:text-3xl text-text-main mb-4 tracking-tight">
                Our Vision
              </h3>
              <p className="text-text-muted font-body leading-relaxed">
                To offer professional services through the principle of &ldquo;Building Partnerships&rdquo; to
                ensure client satisfaction by meeting their core project objectives and becoming the
                preferred construction partner across South Africa.
              </p>
            </motion.div>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate={missionInView ? 'visible' : 'hidden'}
            className="mt-16"
          >
            <motion.span variants={fadeUpVariants} className="overline">
              03 // Our Values
            </motion.span>
            <motion.h3
              variants={fadeUpVariants}
              className="font-heading font-light text-3xl md:text-4xl text-text-main mt-4 tracking-tight"
            >
              What We Stand For
            </motion.h3>

            <motion.div
              variants={staggerContainer}
              initial="hidden"
              animate={missionInView ? 'visible' : 'hidden'}
              className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
            >
              {values.map((value) => (
                <motion.div key={value.title} variants={fadeUpVariants}>
                  <value.icon className="text-primary mb-4" size={28} strokeWidth={1} />
                  <h4 className="font-heading text-xl text-text-main mb-3 tracking-tight">
                    {value.title}
                  </h4>
                  <p className="text-text-muted font-body text-sm leading-relaxed">
                    {value.description}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Compliance & Credentials */}
      <section
        ref={credentialsRef}
        data-testid="credentials-section"
        className="py-24 md:py-32 lg:py-48 bg-background"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate={credentialsInView ? 'visible' : 'hidden'}
          >
            <motion.span variants={fadeUpVariants} className="overline">
              04 // Compliance & Credentials
            </motion.span>
            <motion.h2
              variants={fadeUpVariants}
              className="font-heading font-light text-4xl md:text-5xl text-text-main mt-4 tracking-tight"
            >
              Certified & <span className="text-primary">Compliant</span>
            </motion.h2>
            <motion.p
              variants={fadeUpVariants}
              className="mt-6 max-w-2xl text-text-muted font-body leading-relaxed"
            >
              Every accreditation reflects our commitment to professional standards, safety, and
              trust.
            </motion.p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate={credentialsInView ? 'visible' : 'hidden'}
            className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {credentials.map((credential) => (
              <motion.div
                key={credential.title}
                variants={fadeUpVariants}
                className="bg-surface border border-border p-8 md:p-10"
              >
                <credential.icon className="text-primary mb-4" size={28} strokeWidth={1} />
                <h3 className="font-heading text-xl md:text-2xl text-text-main mb-2 tracking-tight">
                  {credential.title}
                </h3>
                <p className="text-text-muted font-body text-sm leading-relaxed">
                  {credential.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Leadership & Contact */}
      <section
        ref={teamRef}
        data-testid="leadership-section"
        className="py-24 md:py-32 bg-surface border-y border-border"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate={teamInView ? 'visible' : 'hidden'}
          >
            <motion.span variants={fadeUpVariants} className="overline">
              05 // Leadership & Contact
            </motion.span>
            <motion.h2
              variants={fadeUpVariants}
              className="font-heading font-light text-4xl md:text-5xl text-text-main mt-4 tracking-tight"
            >
              Meet the <span className="text-primary">Team</span>
            </motion.h2>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate={teamInView ? 'visible' : 'hidden'}
            className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {team.map((member) => (
              <motion.div
                key={member.name}
                variants={fadeUpVariants}
                className="bg-background border border-border p-8 md:p-12 text-center"
              >
                <div className="w-24 h-24 mx-auto mb-6 border border-primary/40 flex items-center justify-center">
                  <span className="font-heading text-3xl text-primary">{member.initials}</span>
                </div>
                <h3 className="font-heading text-2xl text-text-main mb-2 tracking-tight">
                  {member.name}
                </h3>
                <p className="text-text-muted font-body text-sm leading-relaxed mb-6">
                  {member.role}
                </p>
                <a
                  href={`tel:${member.tel}`}
                  data-testid={`team-phone-${member.initials.toLowerCase()}`}
                  className="inline-flex items-center gap-2 text-primary font-mono text-xs uppercase tracking-widest hover:gap-3 transition-all"
                >
                  <Phone size={14} />
                  {member.phone}
                </a>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-background">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <span className="overline">Work With Us</span>
            <h2 className="font-heading font-light text-4xl md:text-5xl text-text-main mt-4 tracking-tight">
              Let&apos;s Build<br />
              <span className="text-primary">A Partnership</span>
            </h2>
            <p className="mt-6 text-text-muted font-body text-base leading-relaxed max-w-xl mx-auto">
              Whether you&apos;re an insurer, broker, or developer, our team is here to deliver quality
              outcomes you can count on.
            </p>
            <div className="mt-12">
              <Link
                to="/contact"
                data-testid="about-cta-btn"
                className="btn-primary border border-primary text-primary px-8 py-4 font-mono text-xs uppercase tracking-widest hover:text-background transition-colors inline-flex items-center justify-center gap-2"
              >
                Get in Touch
                <ArrowRight size={16} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
